import { Add, Send, TrashCan } from '@carbon/icons-react';
import {
  Button,
  ComboBox,
  FormGroup,
  IconButton,
  MenuButton,
  MenuItem,
  RadioButton,
  RadioButtonGroup,
  Select,
  SelectItem,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  TextArea,
  TextAreaSkeleton,
  TextInput,
  Toggle,
} from '@carbon/react';
import {
  asProblem,
  FormModal,
  hasPermission,
  ListTable,
  PageHeader,
  problemMessage,
  RowAction,
  StatusTag,
  useAdmin,
  useLocalPages,
} from '@pos/admin-ui';
import { ConfirmDialog } from '@pos/design-system';
import { fold, useTranslation } from '@pos/i18n';
import { CUSTOMER_LANGUAGES } from '@pos/i18n/storefront';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useRef, useState } from 'react';
import './messaging.scss';
import { ErrorNotice, TransientNotice } from '../common/Notices';
import { brandsKey, listAllPages, scopeApis } from '../delivery/deliveryApi';
import { BrandEmailPanel } from './BrandEmailPanel';
import { cleanSender, isEmail, MAX_BODY, MAX_SUBJECT, PLAIN_LAYOUT, senderValid } from './emailModel';
import { EmailPreview } from './EmailPreview';
import { EmailSenderFields } from './EmailSenderFields';
import { MessageRoutesPanel } from './MessageRoutesPanel';

// The rich text editor (Tiptap) loads with the first email template opened, not with HO admin.
const RichTextEditor = lazy(() => import('../content/RichTextEditor').then((m) => ({ default: m.RichTextEditor })));
import {
  brandEmailKey,
  deleteTemplate,
  getBrandEmail,
  listTemplates,
  MARKETING_EVENT,
  MESSAGE_CHANNELS,
  MESSAGE_VALUES,
  MESSAGING_MANAGE,
  SAMPLE_VALUES,
  saveTemplate,
  sendTestMessage,
  SMS_MAX,
  TEMPLATE_EVENTS,
  templatesKey,
  type EmailSender,
  type MessageChannel,
  type MessageTemplate,
  type TemplateEvent,
} from './messagingApi';

/** A language's own name ("Tiếng Việt", "English"), so a language added later needs no label (ADR 0071). */
const languageName = (code: string) => new Intl.DisplayNames([code], { type: 'language' }).of(code) ?? code;

/** A template's message as staff read it: a marketing template by its name. */
function useEventLabel() {
  const { t } = useTranslation();
  return (m: MessageTemplate): string =>
    m.eventCode === MARKETING_EVENT && m.name ? m.name : String(t(`messaging.events.${m.eventCode as TemplateEvent}`));
}

/**
 * What a brand's customers are told (ADR 0072, FR-ONL-20, ADR 0125), on three tabs: the templates (per message, channel and
 * language: the Zalo ZNS template Zalo approved, the SMS text, or the email's subject and rich text; and named marketing emails), the
 * channels each message goes by, and the brand's email settings. A message without a template on any channel isn't sent.
 */
export function MessageTemplatesPage() {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canEdit = hasPermission(session, MESSAGING_MANAGE);
  const brands = useQuery({ queryKey: brandsKey, queryFn: () => listAllPages(scopeApis.brands) });
  const [brandId, setBrandId] = useState<string>();
  const brand = brandId ?? brands.data?.[0]?.id;
  const [tab, setTab] = useState(0);

  return (
    <section className="pos-admin-page">
      <PageHeader title={t('messaging.templates.title')} description={t('messaging.templates.intro')} />
      {brands.error && <ErrorNotice title={problemMessage(t, asProblem(brands.error))} onRetry={() => void brands.refetch()} />}
      <Tabs selectedIndex={tab} onChange={({ selectedIndex }) => setTab(selectedIndex)}>
        <TabList aria-label={t('messaging.templates.title')} contained>
          <Tab>{t('messaging.templates.tabs.templates')}</Tab>
          <Tab>{t('messaging.templates.tabs.channels')}</Tab>
          <Tab>{t('messaging.templates.tabs.email')}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            {tab === 0 && (
              <Stack gap={5}>
                <BrandPicker brands={brands.data} brand={brand} onChange={setBrandId} id="message-templates-brand" />
                {brand && <TemplatesList brand={brand} canEdit={canEdit} />}
              </Stack>
            )}
          </TabPanel>
          <TabPanel>{tab === 1 && <MessageRoutesPanel canEdit={canEdit} />}</TabPanel>
          <TabPanel>
            {tab === 2 && (
              <Stack gap={5}>
                <BrandPicker brands={brands.data} brand={brand} onChange={setBrandId} id="brand-email-brand" />
                {brand && <BrandEmailPanel brandId={brand} canEdit={canEdit} />}
              </Stack>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </section>
  );
}

type Brand = { id: string; name: string };

function BrandPicker({ id, brands, brand, onChange }: { id: string; brands?: Brand[]; brand?: string; onChange(id: string): void }) {
  const { t } = useTranslation();
  return (
    <div className="pos-messages__filters">
      <ComboBox
        id={id}
        titleText={t('messaging.templates.brand')}
        items={brands ?? []}
        itemToString={(b) => b?.name ?? ''}
        selectedItem={brands?.find((b) => b.id === brand) ?? null}
        onChange={({ selectedItem }) => selectedItem && onChange(selectedItem.id)}
      />
    </div>
  );
}

function TemplatesList({ brand, canEdit }: { brand: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const eventLabel = useEventLabel();
  const queryClient = useQueryClient();
  const templates = useQuery({ queryKey: templatesKey(brand), queryFn: () => listTemplates(brand) });
  const [editing, setEditing] = useState<MessageTemplate | 'new'>();
  const [deleting, setDeleting] = useState<MessageTemplate>();
  const [testing, setTesting] = useState<MessageTemplate>();
  const [notice, setNotice] = useState<string>();
  const [search, setSearch] = useState('');
  const query = fold(search.trim());
  const pages = useLocalPages(
    (templates.data ?? []).filter(
      (m) =>
        !query ||
        fold(`${eventLabel(m)} ${t(`messaging.channels.${m.channel as MessageChannel}`)} ${languageName(m.language)} ${m.providerTemplateId ?? ''} ${m.body ?? ''} ${m.subject ?? ''}`).includes(query),
    ),
  );
  const remove = useMutation({
    mutationFn: (template: MessageTemplate) => deleteTemplate(template.id),
    onSuccess: async () => {
      setDeleting(undefined);
      await queryClient.invalidateQueries({ queryKey: templatesKey(brand) });
    },
  });

  return (
    <>
      {notice && <TransientNotice kind="success" title={notice} onDone={() => setNotice(undefined)} />}
      <ListTable<MessageTemplate>
          ariaLabel={t('messaging.templates.title')}
          loading={templates.isLoading}
          error={templates.error ? problemMessage(t, asProblem(templates.error)) : undefined}
          onRetry={() => void templates.refetch()}
          rows={pages.rows}
          pagination={pages.pagination}
          // As on every list screen: search and Create in the table's toolbar, a row opens for editing.
          search={{
            label: t('admin.search'),
            onChange: (value) => {
              setSearch(value);
              pages.reset();
            },
          }}
          toolbarActions={
            canEdit ? (
              <Button renderIcon={Add} onClick={() => setEditing('new')}>
                {t('admin.create')}
              </Button>
            ) : undefined
          }
          onRowClick={canEdit ? (m) => setEditing(m) : undefined}
          empty={search ? undefined : { heading: t('messaging.templates.empty') }}
          columns={[
            { key: 'eventCode', header: t('messaging.templates.columns.event'), render: (m) => eventLabel(m) },
            {
              key: 'purpose',
              header: t('messaging.templates.columns.purpose'),
              render: (m) =>
                m.purpose === 'marketing' ? (
                  <Tag type="magenta" size="sm">{t('messaging.templates.purpose.marketing')}</Tag>
                ) : (
                  <Tag type="cool-gray" size="sm">{t('messaging.templates.purpose.service')}</Tag>
                ),
            },
            { key: 'channel', header: t('messaging.templates.columns.channel'), render: (m) => t(`messaging.channels.${m.channel as MessageChannel}`) },
            { key: 'language', header: t('messaging.templates.columns.language'), render: (m) => languageName(m.language) },
            { key: 'content', header: t('messaging.templates.columns.content'), render: (m) => (m.channel === 'email' ? m.subject : (m.providerTemplateId ?? m.body)) ?? '' },
            {
              key: 'isActive',
              header: t('messaging.templates.columns.status'),
              render: (m) => (
                <StatusTag status={m.isActive ? 'active' : 'inactive'}>{t(m.isActive ? 'delivery.providers.active' : 'delivery.providers.inactive')}</StatusTag>
              ),
            },
          ]}
          rowActions={
            canEdit
              ? (m) => (
                  <>
                    <RowAction action={t('messaging.test.action')} record={eventLabel(m)} icon={Send} onClick={() => setTesting(m)} />
                    <RowAction action={t('admin.delete')} record={eventLabel(m)} icon={TrashCan} onClick={() => setDeleting(m)} />
                  </>
                )
              : undefined
          }
        />
      {editing && (
        <TemplateDialog
          brandId={brand}
          template={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(undefined)}
          onSaved={() => {
            setEditing(undefined);
            void queryClient.invalidateQueries({ queryKey: templatesKey(brand) });
          }}
        />
      )}
      {testing && (
        <TestDialog
          template={testing}
          onClose={() => setTesting(undefined)}
          onSent={() => {
            setTesting(undefined);
            setNotice(t('messaging.test.queued'));
          }}
        />
      )}
      <ConfirmDialog
        open={!!deleting}
        title={t('messaging.templates.deleteTitle')}
        confirmLabel={t('common.delete')}
        busy={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(undefined)}
      >
        {t('messaging.templates.deleteBody')}
      </ConfirmDialog>
    </>
  );
}

function TemplateDialog({ brandId, template, onClose, onSaved }: { brandId: string; template?: MessageTemplate; onClose(): void; onSaved(): void }) {
  const { t } = useTranslation();
  const [eventCode, setEventCode] = useState<TemplateEvent>((template?.eventCode as TemplateEvent | undefined) ?? 'order_accepted');
  const [channel, setChannel] = useState<MessageChannel>((template?.channel as MessageChannel | undefined) ?? 'zalo_zns');
  const [language, setLanguage] = useState(template?.language ?? CUSTOMER_LANGUAGES[0] ?? 'vi');
  const [templateId, setTemplateId] = useState(template?.providerTemplateId ?? '');
  const [body, setBody] = useState(template?.body ?? '');
  const [isActive, setIsActive] = useState(template?.isActive ?? true);
  const [parameters, setParameters] = useState<ParameterRow[]>(() =>
    Object.entries(template?.parameters ?? {}).map(([name, value], key) => ({ key, name, value })),
  );
  const [name, setName] = useState(template?.name ?? '');
  const [subject, setSubject] = useState(template?.subject ?? '');
  const [sender, setSender] = useState<EmailSender>({
    accountId: template?.accountId ?? null,
    fromName: template?.fromName ?? null,
    fromAddress: template?.fromAddress ?? null,
    replyTo: template?.replyTo ?? null,
  });
  const [submitted, setSubmitted] = useState(false);
  const marketing = eventCode === MARKETING_EVENT;
  // Marketing templates are emails (ADR 0125).
  const email = channel === 'email' || marketing;
  const layout = useQuery({ queryKey: brandEmailKey(brandId), queryFn: () => getBrandEmail(brandId), enabled: email });
  const badParameters = channel === 'zalo_zns' && parameters.some((p) => !parameterValid(p));
  const missing = email
    ? !subject.trim() || !body.trim() || body.length > MAX_BODY || !senderValid(sender) || (marketing && !name.trim())
    : (channel === 'zalo_zns' ? !templateId.trim() : !body.trim()) || badParameters;
  const save = useMutation({
    mutationFn: () =>
      saveTemplate(template?.id, {
        brandId,
        eventCode,
        channel: email ? 'email' : channel,
        language,
        providerTemplateId: channel === 'zalo_zns' && !email ? templateId.trim() : null,
        // An email's body is its rich text (the website's Markdown subset with {value}s, ADR 0125); an SMS's its words.
        body: email || channel === 'sms' ? body.trim() : null,
        parameters: channel === 'zalo_zns' && !email ? Object.fromEntries(parameters.map((p) => [p.name.trim(), p.value.trim()])) : {},
        isActive,
        ...(email ? { subject: subject.trim(), ...cleanSender(sender), ...(marketing ? { name: name.trim() } : {}) } : {}),
      }),
    onSuccess: onSaved,
  });
  const values = MESSAGE_VALUES[eventCode].map((v) => `{${v}}`).join(', ');
  /** The server's reason; for a value the message doesn't have, which ones (the `values` detail). */
  const saveProblem = (error: unknown) => {
    const problem = asProblem(error);
    const message = problemMessage(t, problem);
    return problem?.code === 'integration.template_value_unknown' && typeof problem.values === 'string' ? `${message} (${problem.values})` : message;
  };
  const valueLabel = (name: string) => String(t(`messaging.values.${name}`, { defaultValue: name }));
  const sms = useRef<HTMLTextAreaElement>(null);
  /** Puts `{name}` where the cursor is in the SMS text. */
  const insertValue = (name: string) => {
    const el = sms.current;
    const at = el?.selectionStart ?? body.length;
    const end = el?.selectionEnd ?? at;
    setBody(`${body.slice(0, at)}{${name}}${body.slice(end)}`);
  };

  return (
    <FormModal
      title={template ? t('admin.edit') : t('admin.create')}
      label={t('messaging.templates.title')}
      size={email ? 'lg' : 'md'}
      pending={save.isPending}
      error={save.error ? saveProblem(save.error) : null}
      onSubmit={() => {
        setSubmitted(true);
        if (!missing) save.mutate();
      }}
      onClose={onClose}
    >
      <Select
        id="template-event"
        labelText={t('messaging.templates.columns.event')}
        value={eventCode}
        onChange={(e) => {
          const next = e.target.value as TemplateEvent;
          setEventCode(next);
          if (next === MARKETING_EVENT) setChannel('email');
        }}
      >
        {TEMPLATE_EVENTS.map((m) => (
          <SelectItem key={m} value={m} text={t(`messaging.events.${m}`)} />
        ))}
      </Select>
      {marketing && (
        <TextInput
          id="template-name"
          labelText={t('messaging.templates.name')}
          helperText={t('messaging.templates.nameHelp')}
          value={name}
          maxLength={100}
          required
          invalid={submitted && !name.trim()}
          invalidText={t('admin.validation.required')}
          onChange={(e) => setName(e.target.value)}
        />
      )}
      <RadioButtonGroup
        legendText={t('messaging.templates.columns.channel')}
        name="template-channel"
        valueSelected={email ? 'email' : channel}
        helperText={marketing ? t('messaging.templates.marketingEmailOnly') : undefined}
        onChange={(value) => {
          // Another channel asks for another field: its emptiness shows only after the next save.
          setChannel(value as MessageChannel);
          setSubmitted(false);
        }}
      >
        {MESSAGE_CHANNELS.map((c) => (
          <RadioButton key={c} id={`template-channel-${c}`} value={c} labelText={t(`messaging.channels.${c}`)} disabled={marketing && c !== 'email'} />
        ))}
      </RadioButtonGroup>
      <Select id="template-language" labelText={t('messaging.templates.columns.language')} value={language} onChange={(e) => setLanguage(e.target.value)}>
        {CUSTOMER_LANGUAGES.map((l) => (
          <SelectItem key={l} value={l} text={languageName(l)} />
        ))}
      </Select>
      {email ? (
        <div className="pos-email-template">
          <Stack gap={6} className="pos-email-template__fields">
            <TextInput
              id="template-subject"
              labelText={t('messaging.email.subject')}
              helperText={t('messaging.templates.smsHelp', { values })}
              value={subject}
              maxLength={MAX_SUBJECT}
              required
              invalid={submitted && !subject.trim()}
              invalidText={t('admin.validation.required')}
              onChange={(e) => setSubject(e.target.value)}
            />
            <Suspense fallback={<TextAreaSkeleton />}>
              {/* A message's own values: the editor starts again when the message changes. */}
              <RichTextEditor
                key={eventCode}
                id="template-email-body"
                label={t('messaging.email.body')}
                value={body}
                brandId={brandId}
                mode="email"
                placeholders={MESSAGE_VALUES[eventCode].map((name) => ({ name, label: valueLabel(name) }))}
                helperText={MESSAGE_VALUES[eventCode].length ? t('messaging.email.bodyHelp') : t('messaging.email.bodyHelpNoValues')}
                invalidText={submitted && !body.trim() ? t('admin.validation.required') : undefined}
                onChange={setBody}
              />
            </Suspense>
            <EmailSenderFields
              id="template-sender"
              legend={t('messaging.email.sender.override')}
              brandId={brandId}
              value={sender}
              onChange={setSender}
              submitted={submitted}
              defaultLabel={t('messaging.email.sender.brandDefault')}
            />
          </Stack>
          <div className="pos-email-template__preview">
            <h3 className="pos-email-preview__heading">{t('messaging.email.preview.title')}</h3>
            <p className="pos-messages__secondary">{t('messaging.email.preview.help')}</p>
            <EmailPreview layout={layout.data ?? PLAIN_LAYOUT} subject={subject} body={body} values={SAMPLE_VALUES} marketing={marketing} />
          </div>
        </div>
      ) : channel === 'zalo_zns' ? (
        <>
          <TextInput
            id="template-zns"
            labelText={t('messaging.templates.znsId')}
            helperText={t('messaging.templates.znsHelp')}
            value={templateId}
            maxLength={100}
            required
            invalid={submitted && !templateId.trim()}
            invalidText={t('admin.validation.required')}
            onChange={(e) => setTemplateId(e.target.value)}
          />
          <ParametersEditor rows={parameters} values={values} submitted={submitted} onChange={setParameters} />
        </>
      ) : (
        <TextArea
          id="template-sms"
          labelText={t('messaging.templates.smsBody')}
          helperText={t('messaging.templates.smsHelp', { values })}
          value={body}
          rows={4}
          maxCount={SMS_MAX}
          enableCounter
          required
          invalid={submitted && !body.trim()}
          invalidText={t('admin.validation.required')}
          onChange={(e) => setBody(e.target.value)}
          ref={sms}
        />
      )}
      {channel === 'sms' && !email && MESSAGE_VALUES[eventCode].length > 0 && (
        <div>
          <MenuButton label={t('cms.editor.insertValue')} kind="ghost" size="sm" menuAlignment="bottom-start">
            {MESSAGE_VALUES[eventCode].map((name) => (
              <MenuItem key={name} label={`${valueLabel(name)} {${name}}`} onClick={() => insertValue(name)} />
            ))}
          </MenuButton>
        </div>
      )}
      <Toggle
        id="template-active"
        labelText={t('messaging.templates.columns.status')}
        labelA={t('delivery.providers.inactive')}
        labelB={t('delivery.providers.active')}
        toggled={isActive}
        onToggle={setIsActive}
      />
    </FormModal>
  );
}

type ParameterRow = { key: number; name: string; value: string };

/** A parameter name as Zalo approves it, and something to put in it (the server checks the same). */
const PARAMETER_NAME = /^[A-Za-z0-9_]{1,50}$/;
const parameterValid = (p: ParameterRow) => PARAMETER_NAME.test(p.name.trim()) && p.value.trim().length > 0 && p.value.length <= 200;

/**
 * A ZNS template's parameters (ADR 0111): each brand registers its own template with Zalo, naming the parameters as it likes, so
 * each name here is filled like an SMS text from the message's values. With none, the values go under their own names.
 */
function ParametersEditor({ rows, values, submitted, onChange }: { rows: ParameterRow[]; values: string; submitted: boolean; onChange(rows: ParameterRow[]): void }) {
  const { t } = useTranslation();
  const set = (key: number, change: Partial<ParameterRow>) => onChange(rows.map((r) => (r.key === key ? { ...r, ...change } : r)));
  return (
    <FormGroup legendText={t('messaging.templates.parameters.title')}>
      <p className="pos-messages__secondary">{t('messaging.templates.parameters.help', { values })}</p>
      {rows.map((r, index) => (
        <div key={r.key} className="pos-messages__parameter">
          <TextInput
            id={`template-parameter-name-${r.key}`}
            labelText={t('messaging.templates.parameters.name')}
            hideLabel={index > 0}
            value={r.name}
            maxLength={50}
            invalid={submitted && !PARAMETER_NAME.test(r.name.trim())}
            invalidText={t('admin.validation.required')}
            onChange={(e) => set(r.key, { name: e.target.value })}
          />
          <TextInput
            id={`template-parameter-value-${r.key}`}
            labelText={t('messaging.templates.parameters.value')}
            hideLabel={index > 0}
            value={r.value}
            maxLength={200}
            invalid={submitted && !r.value.trim()}
            invalidText={t('admin.validation.required')}
            onChange={(e) => set(r.key, { value: e.target.value })}
          />
          <IconButton
            label={`${t('messaging.templates.parameters.remove')} ${r.name}`.trim()}
            kind="ghost"
            onClick={() => onChange(rows.filter((x) => x.key !== r.key))}
          >
            <TrashCan />
          </IconButton>
        </div>
      ))}
      <Button
        kind="ghost"
        size="sm"
        renderIcon={Add}
        onClick={() => onChange([...rows, { key: Math.max(-1, ...rows.map((r) => r.key)) + 1, name: '', value: '' }])}
      >
        {t('messaging.templates.parameters.add')}
      </Button>
    </FormGroup>
  );
}

/**
 * A test of one template's message to a phone (an email address for an email template, ADR 0125) typed here, with sample values,
 * through the brand's messaging accounts (ADR 0072):
 * with a test account nothing is delivered, and the message log shows what would have been sent.
 */
function TestDialog({ template, onClose, onSent }: { template: MessageTemplate; onClose(): void; onSent(): void }) {
  const { t } = useTranslation();
  const eventLabel = useEventLabel();
  const [recipient, setRecipient] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const send = useMutation({ mutationFn: () => sendTestMessage(template.id, recipient.trim()), onSuccess: onSent });
  // An email template's test goes to an email address (ADR 0125).
  const email = template.channel === 'email';
  const missing = recipient.trim().length === 0 || (email && !isEmail(recipient.trim()));
  return (
    <FormModal
      title={t('messaging.test.title')}
      label={eventLabel(template)}
      primaryText={t('messaging.test.send')}
      pending={send.isPending}
      error={send.error ? problemMessage(t, asProblem(send.error)) : null}
      onSubmit={() => {
        setSubmitted(true);
        if (!missing) send.mutate();
      }}
      onClose={onClose}
    >
      <p>{t('messaging.test.intro')}</p>
      <TextInput
        id="test-recipient"
        type={email ? 'email' : 'tel'}
        labelText={t(email ? 'messaging.test.emailRecipient' : 'messaging.test.recipient')}
        helperText={t(email ? 'messaging.test.emailRecipientHelp' : 'messaging.test.recipientHelp')}
        value={recipient}
        maxLength={email ? 254 : 20}
        required
        invalid={submitted && missing}
        invalidText={t(email && recipient.trim() ? 'messaging.email.sender.emailInvalid' : 'admin.validation.required')}
        onChange={(e) => setRecipient(e.target.value)}
      />
    </FormModal>
  );
}
